<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
  import { ButtonGroup, Button } from "flowbite-svelte";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import WebViewer from "@pdftron/webviewer";
  // @ts-ignore - ExtractItems2 has @ts-nocheck, prop types not inferred
  import ExtractItemsComponent from "./ExtractItems2.svelte"
  import { logInfo, logError, logWarning } from "../../services/logging/log-handler.js";
  
  // Type assertion to allow studyName prop - ExtractItems2 has @ts-nocheck so TypeScript can't infer props
  const ExtractItems = ExtractItemsComponent as any;

  export let documentId: string = "";
  export let documentTitle: string = "Journal of Clinical Oncology";
  export let documentUrl: string = "/pdf/yRpV95gBW4DRgsiTb3fw.pdf";
  export let showExternalLink: boolean = true;
  // export let activeView: "study" | "sheet" = "study";
  export let studyName: string = "Study 1";

  let resolvedDocumentTitle = documentTitle;
  let resolvedDocumentUrl = documentUrl;
  let resolvedStudyName = studyName;

  // Document lookup mapping based on doc_key IDs
  function getDocumentInfo(docKey: string): { title: string; url: string; studyName: string } | null {
    // Remove .pdf extension if present for lookup
    const key = docKey.replace(/\.pdf$/i, '');
    
    // Map doc_key to document title, URL, and study name
    const documentMap: Record<string, { title: string; url: string; studyName: string }> = {
      'cBpU95gBW4DRgsiT_3cn': {
        title: 'Journal of Thorasic Oncology 2014',
        url: '/pdf/cBpU95gBW4DRgsiT_3cn.pdf',
        studyName: 'EVEREST'
      },
      'zhpV95gBW4DRgsiTd3ft': {
        title: 'Journal of Cancer',
        url: '/pdf/zhpV95gBW4DRgsiTd3ft.pdf',
        studyName: 'EVEREST'
      },
      'yRpV95gBW4DRgsiTb3fw': {
        title: 'Journal of Clinical Oncology',
        url: '/pdf/yRpV95gBW4DRgsiTb3fw.pdf',
        studyName: 'MATTERHORN'
      },
      'ixpV95gBW4DRgsiTFndZ': {
        title: 'Japanese Journal of Oncology 2000',
        url: '/pdf/ixpV95gBW4DRgsiTFndZ.pdf',
        studyName: 'MATTERHORN'
      },
      'aBpU95gBW4DRgsiT9Hd_': {
        title: 'Cancer Journal 2000',
        url: '/pdf/aBpU95gBW4DRgsiT9Hd_.pdf',
        studyName: 'MATTERHORN'
      }
    };

    return documentMap[key] || null;
  }

  // Look up document info using doc_key
  function lookupDocumentInfo() {
    if (!documentId) {
      // No document ID, use provided values or defaults
      resolvedDocumentTitle = documentTitle;
      resolvedDocumentUrl = documentUrl;
      resolvedStudyName = studyName;
      return;
    }

    // Try to get info from the mapping
    const docInfo = getDocumentInfo(documentId);
    
    if (docInfo) {
      resolvedDocumentTitle = docInfo.title;
      resolvedDocumentUrl = docInfo.url;
      // Only update study name if it's the default "Study 1" (meaning it wasn't explicitly provided)
      if (studyName === "Study 1") {
        resolvedStudyName = docInfo.studyName;
      } else {
        resolvedStudyName = studyName;
      }
    } else {
      // Fallback: use provided props or construct from documentId
      resolvedDocumentTitle = documentTitle || "Untitled Document";
      resolvedDocumentUrl = documentUrl || (documentId.endsWith('.pdf') ? `/pdf/${documentId}` : `/pdf/${documentId}.pdf`);
      resolvedStudyName = studyName;
    }
  }

  // Lookup document info when documentId changes
  $: if (documentId) {
    lookupDocumentInfo();
  }

  // Update resolved values when props change (if explicitly provided)
  $: if (documentTitle && documentTitle !== "Journal of Clinical Oncology") {
    resolvedDocumentTitle = documentTitle;
  }
  $: if (documentUrl && documentUrl !== "/pdf/yRpV95gBW4DRgsiTb3fw.pdf") {
    resolvedDocumentUrl = documentUrl;
  }
  $: if (studyName && studyName !== "Study 1") {
    resolvedStudyName = studyName;
  }

  const ALL_HEADER_ELEMENTS: string[] = [
    /* ───────── CONTAINER ROWS ───────── */
    'header',                     // legacy
    'toolsHeader',                // legacy – 2nd row
    'default-top-header',         // modular
    'default-tools-header',       // modular

    'leftPanelButton',
    'menuButton',
    'openFileButton', 'filePickerButton',
    'saveButton',
    'printButton', 'downloadButton',
    'searchButton',
    'selectToolButton', 'panToolButton', 'marqueeToolButton',
    'viewControlsButton', 'viewControlsOverlay',
    'zoomOutButton', 'zoomInButton', 'zoomOverlayButton', 'zoomDropdown',
    'fitWidthButton', 'fitPageButton',
    'previousPageButton', 'nextPageButton',
    'currentPageSelector', 'pageNavOverlayButton',
    'rotateClockwiseButton', 'rotateCounterClockwiseButton',
    'newTabButton',
    'languageButton', 'languageOverlay',

    /* ───────── PANEL / TAB HEADERS ───────── */
    'leftPanel', 'outlinePanel', 'thumbnailsPanel', 'notesPanel',
    'layersPanel', 'bookmarkPanel', 'signaturePanel',
    'annotationsPanel', 'comparePanel',
    'searchPanel',

    /* ───────── OVERFLOWS & POPUPS ───────── */
    'menuOverlay', 'toolsOverlay', 'searchOverlay',
    'contextMenuPopup', 'textPopup',
    'ribbonsModal', 'viewControlsOverlay',
    'pageManipulationOverlay', 'cropPagePopup',
    'stylePopup',

    /* ───────── RIBBON / TOOLBAR-GROUP "virtual" elements ───────── */
    //  (present in both UIs – hiding them prevents auto-injection)
    'toolbarGroup-Annotate',
    'toolbarGroup-Insert',
    'toolbarGroup-Text',
    'toolbarGroup-Shapes',
    'toolbarGroup-Measure',
    'toolbarGroup-View',
    'toolbarGroup-Edit',
    'toolbarGroup-UndoRedo',
    'toolbarGroup-FillAndSign',
    'toolbarGroup-Redaction',
    'toolbarGroup-Forms',
    'toolbarGroup-PageManipulation',
    'toolbarGroup-Tools',
    'toolbarGroup-Save',
    'toolbarGroup-FavoriteTools',
    'toolbarGroup-NotesPanel',
    'toolbarGroup-MiscTools',
    'toolbarGroup-Ribbons',
    'toolbarGroup-Search',
    'toolbarGroup-Snapshots',
    'toolbarGroup-Download',
    'toolbarGroup-Print',
    'tools-header',
    'copyOption',
    'downloadOption',
    /* ───────── SNAPSHOT / SNIP SPECIFICS ───────── */
    'snapshotToolButton', 'snippingButton',
    'copySnapshotButton', 'downloadSnapshotButton'
  ];

  let viewerDiv: HTMLDivElement;
  let webViewerInstance: any;
  let splitPosition: number = 50;
  let lastDocumentUrl: string | null = null;
  let isInitializing = false;
  let initializationPromise: Promise<any> | null = null;

  // Properly dispose of WebViewer instance
  function disposeWebViewer() {
    if (webViewerInstance) {
      try {
        // Dispose the UI first
        if (webViewerInstance.UI) {
          webViewerInstance.UI.dispose();
        }
        // Clear the document viewer
        if (webViewerInstance.Core && webViewerInstance.Core.documentViewer) {
          webViewerInstance.Core.documentViewer.destroy();
        }
      } catch (e) {
        logWarn("Error disposing WebViewer", "DocumentExtract", undefined, { error: e });
      }
      webViewerInstance = null;
    }
    isInitializing = false;
    initializationPromise = null;
  }

  // Initialize WebViewer with proper guards
  async function initializeWebViewer(url: string) {
    // Prevent multiple simultaneous initializations
    if (isInitializing || !viewerDiv || !url) {
      return;
    }

    // If URL hasn't changed, don't reinitialize
    if (url === lastDocumentUrl && webViewerInstance) {
      return;
    }

    // Dispose existing instance
    disposeWebViewer();

    // Clear the div content to ensure clean state
    if (viewerDiv) {
      viewerDiv.innerHTML = '';
    }

    isInitializing = true;
    lastDocumentUrl = url;

    try {
      // Cast to any to satisfy TS compiler until proper types are resolved
      initializationPromise = (WebViewer as any)(
        {
          path: "/webviewer/lib",
          licenseKey: 'demo:1747054245387:61ee4418030000000045f46a9d069b2457a2249f25995dbc715545b107',
          initialDoc: url
        },
        viewerDiv
      );

      const instance = await initializationPromise;
      
      // Check if we're still supposed to use this instance (URL might have changed)
      if (url !== lastDocumentUrl) {
        // URL changed during initialization, dispose this instance
        try {
          if (instance.UI) instance.UI.dispose();
          if (instance.Core && instance.Core.documentViewer) {
            instance.Core.documentViewer.destroy();
          }
        } catch (e) {
          logWarn("Error disposing WebViewer after URL change", "DocumentExtract", undefined, { error: e });
        }
        return;
      }

      webViewerInstance = instance;

      instance.UI.disableElements(ALL_HEADER_ELEMENTS);

      const snipTool = instance.Core.documentViewer.getTool('SnippingTool');
      snipTool.setSnippingMode(
        instance.Core.Tools.SnippingCreateTool.SnippingModes.CLIPBOARD
      );

      snipTool.addEventListener('snippingApplied', () => {
        instance.UI.setToolMode('Pan');
      });

      isInitializing = false;
    } catch (error) {
      logError(error, "DocumentExtract", undefined, { message: "Error initializing WebViewer" });
      isInitializing = false;
      webViewerInstance = null;
    }
  }

  // Initialize when viewerDiv is available and URL changes
  $: if (viewerDiv && resolvedDocumentUrl) {
    initializeWebViewer(resolvedDocumentUrl);
  }

  // Cleanup on component destroy
  onDestroy(() => {
    disposeWebViewer();
  });

  function openExternal() {
    if (documentId) {
      window.open(`/document-extract?id=${documentId}`, '_blank');
    }
  }

  export function getViewerInstance() {
    return webViewerInstance;
  }
</script>

<Splitpanes class="document-extract-splitpanes">
  <Pane size={splitPosition}>
    <div class="study-extract-panel">
      <div class="panel-header">
        <span class="panel-header-label">Study Extract:</span>
        <span class="panel-header-title">{resolvedStudyName}</span>
        {#if showExternalLink}
          <Button on:click={openExternal} pill={true} outline={true} class="general-button icon-only borderless" size="sm">
            <FontAwesomeIcon icon={faUpRightFromSquare} class="w-4 h-4" />
          </Button>
        {/if}
      </div>       
      <div class="study-extract-content">
          <ExtractItems studyName={resolvedStudyName} />
      </div>
    </div>
  </Pane>
  <Pane size={splitPosition}>
    <div class="document-viewer-panel">
      <div class="panel-header">
        <span class="panel-header-label">Document:</span>
        <span class="panel-header-title">{resolvedDocumentTitle}</span>
      </div>
      <div class="document-viewer-container">
        <div bind:this={viewerDiv} style="height: 100%; width: 100%;"></div>
      </div>
    </div>
  </Pane>
</Splitpanes>
